import { useEffect, useState } from "react";
import {
  cleanObject,
  errorToast,
  formatDateToUsPecificTimezone,
  formatLocalDate,
  formatTimeToUsPecificTimezone,
  objectToFormData,
  successToast,
} from "../../../../utils/helpers";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import TextInput from "../../../../components/common/TextInput";
import DiscardSaveButtons from "../../../../components/tv-app/discard-save-buttons";
import Checkbox from "../../../../components/common/CheckBox";
import Select from "../../../../components/common/Select";
// import DatePicker from "../../../../components/common/DatePicker";
import TimeInput from "../../../../components/common/TimeInput";
import FileInput from "../../../../components/common/FileInput";
import ImageCropModal from "../../../../components/common/CropImage";
// import Button from "../../../../components/common/button";
import useAddOffer from "../../../../hooks/use-add-offer";
import useGetCouponTypesQuery from "../../../../hooks/use-get-coupon-types";
import Spinner from "../../../../components/common/Spinner";
import { MyStayType } from "../../../../utils/types/offers";
import { queryClient } from "../../../../App";
import useGetBusinessReasonsDataQuery from "../../../../hooks/use-get-business-reasons";
import useGetCostCentersDataQuery from "../../../../hooks/use-get-cost-centers-data";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AddEligibilitySvg,
  LeftArrowSvg,
  RemoveEligibilitySvg,
} from "../../../../icons";
import { displayOnPagesEnum, IActivity } from "../../../../utils/types";
import useGetServiceCategoriesQuery from "../../../../hooks/use-get-service-categories";
import useUpdateServices from "../../../../hooks/use-update-services";
import TextArea from "../../../../components/common/TextArea";
import Button from "../../../../components/common/button";
import useAddDraftOffer from "../../../../hooks/use-add-draft-offer";
import useUpdateDraftOffer from "../../../../hooks/use-update-draft-offer";
import DatePicker from "../../../../components/common/DatePicker";
import useGetEligibilityCriteriaQuery from "../../../../hooks/use-get-eligibility-criteria";
import DynamicMultiSelect from "../../../../components/common/DynamicMultiSelect";

type SelectValue = { label?: string; value?: string } | null | undefined;

type EligibilityFieldValue =
  | { label: string; value: string }
  | { label: string; value: string }[]
  | string
  | undefined;

interface IActivityCalendarFormValues {
  id?: string;
  myStayType: MyStayType;
  name: string;
  description: string;
  redemptionDescription: string;
  displayOn: Record<displayOnPagesEnum, boolean>;
  type: SelectValue;
  value?: string | number | null;
  eligibilityCriterias: {
    eligibilityCriteria: {
      label: string;
      value: string;
    } | null;
    data: EligibilityFieldValue;
  }[];
  noOfGuest: string | number | null;
  businessReason: SelectValue;
  redemptionLocation: SelectValue;
  serviceCategory?: SelectValue;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  days: Array<{
    id: number;
    label: string;
    value: string;
  }>;
  image: Blob | string | null;
}

const RESERVATION_NO = "Reservation No";
const MINIMUM_FOLIO_CHARGE = "Minimum Folio Charge";
const DynamicMultiSelectKeys = ["RESERVATION_NO"];

const WEEK_DAYS = [
  { id: 0, value: "Everyday", label: "Everyday" },
  { id: 1, value: "Mon", label: "Monday" },
  { id: 2, value: "Tue", label: "Tuesday" },
  { id: 3, value: "Wed", label: "Wednesday" },
  { id: 4, value: "Thu", label: "Thursday" },
  { id: 5, value: "Fri", label: "Friday" },
  { id: 6, value: "Sat", label: "Saturday" },
  { id: 7, value: "Sun", label: "Sunday" },
];

const CreateService = ({
  activityData = {},
  isEditActivity = false,
  onSuccess,
  isDraftEditOffer,
  createNew,
}: {
  activityData?: Partial<IActivity>;
  isEditActivity?: boolean;
  onSuccess?: () => void;
  isDraftEditOffer?: boolean;
  createNew?: boolean;
}) => {
  const navigate = useNavigate();
  const params = useParams();

  const { data: couponTypesData, isLoading: couponTypesDataLoading } =
    useGetCouponTypesQuery({ myStayType: MyStayType.SERVICE });
  const { data: costCentersData, isLoading: costCenterLoading } =
    useGetCostCentersDataQuery();
  const { data: businessReasonData, isLoading: businessReasonDataLoading } =
    useGetBusinessReasonsDataQuery();
  const { data: serviceCategoriesData, isLoading: serviceCategoriesLoading } =
    useGetServiceCategoriesQuery();
  const {
    data: eligibilityCriteriaData,
    isLoading: eligibilityCriteriaLoading,
  } = useGetEligibilityCriteriaQuery();

  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [disableEligibilityFieldsAddOn] = useState<string[]>([RESERVATION_NO]);
  const [dynamicOptionsReset, setDynamicOptionsReset] = useState(true);
  const [isEverydaySelected, setIsEveryDaySelected] = useState<Boolean>(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const handleDynamicOptionsReset = () => {
    setDynamicOptionsReset(false);
  };

  const businessReasonOptions =
    businessReasonData?.result?.data.map((x) => ({
      label: x.name,
      value: x._id,
    })) || [];

  const redemptionLocationOptions =
    costCentersData?.result?.data
      .map((x) => x?.subCenters || [])
      .flat()
      ?.map((x) => ({
        label: x.name,
        value: x._id,
      })) || [];

  const offerTypeOptions =
    couponTypesData?.result?.data.map((x) => ({
      label: x.name,
      value: x._id,
    })) || [];

  const serviceCategoriesOptions =
    serviceCategoriesData?.result?.data.map((x) => ({
      label: x.name,
      value: x._id,
    })) || [];

  const eligibilityOptions =
    eligibilityCriteriaData?.result?.data
      ?.sort((b, a) => b.viewRank - a.viewRank)
      .map((x) => ({
        label: x.name,
        value: x._id,
      })) || [];

  const [defaultValues, setDefaultValues] =
    useState<IActivityCalendarFormValues>({
      id: "",
      myStayType: MyStayType.SERVICE,
      name: activityData.name || "",
      description: activityData.description || "",
      redemptionDescription: activityData.redemptionDescription || "",
      displayOn: {
        MY_SCHEDULE: false,
        RECOMMENDATIONS: false,
        SERVICES: false,
        ACTIVITIES: false,
      },
      type: activityData.type
        ? {
            label: couponTypesData?.result?.data.find(
              (x) => x._id === activityData?.type
            )?.name,
            value: activityData.type,
          }
        : null,
      value: activityData.value || null,
      eligibilityCriterias: [{ eligibilityCriteria: null, data: "" }],
      noOfGuest: activityData.cost || null,
      businessReason: activityData.businessReason
        ? {
            label: businessReasonData?.result?.data.find(
              (x) => x._id === activityData?.businessReason
            )?.name,
            value: activityData.businessReason,
          }
        : null,
      redemptionLocation: activityData.redemptionLocation
        ? {
            label: costCentersData?.result?.data.find(
              (x) => x._id === activityData?.redemptionLocation
            )?.name,
            value: activityData.redemptionLocation,
          }
        : null,
      serviceCategory: activityData.serviceCategory
        ? {
            label: serviceCategoriesData?.result?.data.find(
              (x) => x._id === activityData?.serviceCategory
            )?.name,
            value: activityData.serviceCategory,
          }
        : null,
      startDate:
        activityData.startDate && !createNew
          ? formatLocalDate(activityData.startDate!)
          : formatDateToUsPecificTimezone(new Date().toISOString()),
      endDate:
        activityData.endDate && !createNew
          ? formatLocalDate(activityData.endDate!)
          : formatDateToUsPecificTimezone(new Date().toISOString()),
      startTime:
        activityData.startTime && !createNew
          ? formatTimeToUsPecificTimezone(activityData.startTime!)
          : new Date().toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
      endTime:
        activityData.endTime && !createNew
          ? formatTimeToUsPecificTimezone(activityData?.endTime)
          : "23:59",
      days: activityData?.days?.length
        ? WEEK_DAYS.filter((x) =>
            activityData.days?.find((day) => day === x.value)
          )
        : [],
      image: activityData.image as string,
    });

  const methods = useForm<IActivityCalendarFormValues>({
    defaultValues,
  });

  const {
    fields: eligibilityCriteriaFields,
    append: appendEligibilityCriteria,
    remove: removeEligibilityCriteria,
  } = useFieldArray({
    control: methods.control,
    name: "eligibilityCriterias",
  });

  const { setValue, watch, handleSubmit } = methods;
  const {
    id,
    days,
    type,
    value,
    eligibilityCriterias,
    noOfGuest,
    startDate,
    endDate,
    startTime,
    image,
  } = watch();

  useEffect(() => {
    if (
      !couponTypesDataLoading &&
      !costCenterLoading &&
      !businessReasonDataLoading &&
      !serviceCategoriesLoading &&
      !eligibilityCriteriaLoading &&
      couponTypesData &&
      costCentersData &&
      businessReasonData &&
      serviceCategoriesData &&
      eligibilityCriteriaData
    ) {
      const eligibilityFormData: IActivityCalendarFormValues["eligibilityCriterias"] =
        [];

      activityData.eligibilityCriterias?.forEach((eligibility: any) => {
        const selectedEligibility = eligibilityCriteriaData.result.data.find(
          (item) => item._id === eligibility.criteriaId
        );

        const valueTypeEligibility = DynamicMultiSelectKeys.includes(
          selectedEligibility?.key || ""
        )
          ? "DYNAMIC_MULTI_SELECT"
          : selectedEligibility?.valueType;

        if (
          selectedEligibility &&
          valueTypeEligibility === "DYNAMIC_MULTI_SELECT"
        ) {
          eligibilityFormData.push({
            eligibilityCriteria: {
              label: selectedEligibility.name,
              value: selectedEligibility._id,
            },
            data: eligibility.values.map((item: string) => ({
              label: item,
              value: item,
            })),
          });
        } else if (
          selectedEligibility &&
          valueTypeEligibility === "SINGLE_SELECT"
        ) {
          eligibilityFormData.push({
            eligibilityCriteria: {
              label: selectedEligibility.name,
              value: selectedEligibility._id,
            },
            data: {
              label: selectedEligibility.options?.find(
                (option: { label: string; value: string }) =>
                  option.value === eligibility.values?.[0]
              )?.label,
              value: eligibility.values?.[0],
            },
          });
        } else if (
          selectedEligibility &&
          valueTypeEligibility === "MULTI_SELECT"
        ) {
          eligibilityFormData.push({
            eligibilityCriteria: {
              label: selectedEligibility.name,
              value: selectedEligibility._id,
            },
            data:
              selectedEligibility.options
                ?.filter((option: { label: string; value: string }) =>
                  eligibility.values?.includes(option.value)
                )
                .map((option: { label: string; value: string }) => ({
                  label: option.label,
                  value: option.value,
                })) || [],
          });
        } else if (selectedEligibility && valueTypeEligibility === "TEXT") {
          eligibilityFormData.push({
            eligibilityCriteria: {
              label: selectedEligibility.name,
              value: selectedEligibility._id,
            },
            data: eligibility.values?.[0] || "",
          });
        } else if (selectedEligibility && valueTypeEligibility === "NONE") {
          eligibilityFormData.push({
            eligibilityCriteria: {
              label: selectedEligibility.name,
              value: selectedEligibility._id,
            },
            data: undefined,
          });
        }
      });

      const defaultValuesData: IActivityCalendarFormValues = {
        id: "",
        myStayType: MyStayType.SERVICE,
        name: activityData.name || "",
        description: activityData.description || "",
        redemptionDescription: activityData.redemptionDescription || "",
        displayOn: {
          MY_SCHEDULE: activityData?.displayOnPages?.includes("MY_SCHEDULE")
            ? true
            : false,
          RECOMMENDATIONS: activityData?.displayOnPages?.includes(
            "RECOMMENDATIONS"
          )
            ? true
            : false,
          SERVICES: activityData?.displayOnPages?.includes("SERVICES")
            ? true
            : false,
          ACTIVITIES: activityData?.displayOnPages?.includes("ACTIVITIES")
            ? true
            : false,
        },
        type: activityData.type
          ? {
              label: couponTypesData?.result?.data.find(
                (x) => x._id === activityData?.type
              )?.name,
              value: activityData.type,
            }
          : null,
        value: activityData.value || null,
        eligibilityCriterias: eligibilityFormData.length
          ? eligibilityFormData
          : [{ eligibilityCriteria: null, data: "" }],
        noOfGuest: activityData.cost || null,
        businessReason: activityData.businessReason
          ? {
              label: businessReasonData?.result?.data.find(
                (x) => x._id === activityData?.businessReason
              )?.name,
              value: activityData.businessReason,
            }
          : null,
        redemptionLocation: activityData.redemptionLocation
          ? {
              label: redemptionLocationOptions?.find(
                (x) => x.value === activityData?.redemptionLocation
              )?.label,
              value: activityData.redemptionLocation,
            }
          : null,
        serviceCategory: activityData.serviceCategory
          ? {
              label: serviceCategoriesData?.result?.data.find(
                (x) => x._id === activityData?.serviceCategory
              )?.name,
              value: activityData.serviceCategory,
            }
          : null,
        startDate:
          activityData.startDate && !createNew
            ? formatLocalDate(activityData.startDate!)
            : formatDateToUsPecificTimezone(new Date().toISOString()),
        endDate:
          activityData.endDate && !createNew
            ? formatLocalDate(activityData.endDate!)
            : formatDateToUsPecificTimezone(new Date().toISOString()),
        startTime:
          activityData.startTime && !createNew
            ? activityData.startTime
            : new Date().toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }),
        endTime:
          activityData.endTime && !createNew ? activityData.endTime : "23:59",
        days: activityData?.days?.length
          ? WEEK_DAYS.filter((x) =>
              activityData.days?.find((day) => day === x.value)
            )
          : [],
        image: activityData.image as string,
      };

      setDefaultValues(defaultValuesData);
      methods.reset(defaultValuesData);
    }
  }, [
    isEditActivity,
    couponTypesDataLoading,
    costCenterLoading,
    businessReasonDataLoading,
    serviceCategoriesLoading,
    eligibilityCriteriaLoading,
    couponTypesData,
    costCentersData,
    businessReasonData,
    serviceCategoriesData,
    eligibilityCriteriaData,
  ]);

  const { mutate: addService, isPending: loading } = useAddOffer();
  const { mutate: updateService, isPending: updateLoading } = useUpdateServices(
    {
      id: activityData._id,
    }
  );
  const { mutate: addDraftService, isPending: addDraftServiceLoading } =
    useAddDraftOffer();
  const { mutate: updateDraftService, isPending: updateDraftServiceLoading } =
    useUpdateDraftOffer();

  const clearValues = () => {
    methods.reset({ ...defaultValues });
  };

  const eligibilityCriteriaAppendField = () => {
    appendEligibilityCriteria({ eligibilityCriteria: null, data: "" });
  };

  const eligibilityCriteriaRemoveField = (index: number) => {
    removeEligibilityCriteria(index);
  };

  const validateEndTime =
    (startDate: string, endDate: string, startTime: string) =>
    (value: string) => {
      if (!startDate || !endDate || !startTime) return true;
      if (startDate !== endDate) return true;
      const [startHours, startMinutes] = startTime.split(":");
      const [endHours, endMinutes] = value.split(":");
      switch (true) {
        case startHours > endHours:
          return "End time should be greater than start time";
        case startHours === endHours && startMinutes > endMinutes:
          return "End time should be greater than start time";
        case startHours === endHours && startMinutes === endMinutes:
          return "End time and start time can not be same";
        default:
          return true;
      }
    };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      setSelectedFileName(file.name);
      setImageFile(file);
      setIsCropModalOpen(true);
      e.target.value = "";
    }
  };

  const handleImageClear = () => {
    setImageFile(null);
    setSelectedFileName("");
    setValue("image", null);
  };

  const handleCropComplete = (croppedImageBlob: Blob | null) => {
    if (croppedImageBlob) {
      const file = new File([croppedImageBlob], imageFile?.name!, {
        type: croppedImageBlob?.type,
        lastModified: imageFile?.lastModified,
      });
      setValue("image", file);
    } else {
      const fileInput = document.getElementById(id || "services-img");
      if (fileInput) {
        (fileInput as HTMLInputElement).value = "";
      }
      if (isEditActivity) {
        setValue("image", null);
      }
      handleImageClear();
    }
    setIsCropModalOpen(false);
  };

  const formatEligibilityPayload = (data: IActivityCalendarFormValues) =>
    data.eligibilityCriterias
      .filter((item) => item.eligibilityCriteria?.value)
      .map((item) => ({
        criteriaId: item.eligibilityCriteria?.value,
        ...(item.data
          ? {
              values: Array.isArray(item.data)
                ? item.data.map((option) => option.value)
                : typeof item.data === "string"
                ? [item.data]
                : [item.data?.value],
            }
          : {}),
      }));

  const saveAsDraft = () => {
    const data = watch();
    let payload: any = {
      ...data,
      displayOnPages: Object.keys(data.displayOn).filter(
        (key) => data.displayOn[key as keyof typeof data.displayOn]
      ),
      serviceCategory: data.serviceCategory?.value,
      type: data.type?.value,
      cost: data.noOfGuest,
      eligibilityCriterias: formatEligibilityPayload(data),
      businessReason: data.businessReason?.value,
      redemptionLocation: data.redemptionLocation?.value,
      days: days
        .filter((ele: any) => ele.id !== 0 && ele.value !== "Everyday")
        .map((element: any) => element.value),
    };

    if (typeof payload.image === "string") {
      payload.image = null;
    }

    if (!!activityData?._id) {
      payload.stayId = activityData._id;
    }

    cleanObject(payload);
    if (isDraftEditOffer) {
      updateDraftService(
        { data: objectToFormData(payload), id: activityData._id },
        {
          onSuccess: async (res) => {
            successToast(res.result.message);
            onSuccess?.();
          },
          onError: (err) => {
            errorToast(err?.response?.data?.result?.message);
          },
        }
      );
    } else {
      addDraftService(objectToFormData(payload), {
        onSuccess: async (res) => {
          successToast(res.result.message);
          onSuccess?.();
          navigate(`/${params.hotelcode}/tv-app/my-stay/services`);
        },
        onError: (err) => {
          errorToast(err?.response?.data?.result?.message);
        },
      });
    }
  };

  const onSubmit = (data: IActivityCalendarFormValues) => {
    let payload: any = {
      myStayType: MyStayType.SERVICE,
      name: data.name,
      description: data.description,
      redemptionDescription: data.redemptionDescription,
      displayOnPages: Object.keys(data.displayOn).filter(
        (key) => data.displayOn[key as keyof typeof data.displayOn]
      ),
      type: data.type?.value,
      value: data.value || 0,
      cost: data.noOfGuest || 1,
      businessReason: data.businessReason?.value,
      redemptionLocation: data.redemptionLocation?.value,
      serviceCategory: data.serviceCategory?.value,
      eligibilityCriterias: formatEligibilityPayload(data),
      startDate: data.startDate,
      endDate: data.endDate,
      startTime: data.startTime,
      endTime: data.endTime,
      days: days
        .filter((ele: any) => ele.id !== 0 && ele.value !== "Everyday")
        .map((element: any) => element.value),
      image: data.image || (activityData.image as string),
    };

    if (typeof data.image === "string" && !isEditActivity) {
      payload.image = null;
    }
    if (isDraftEditOffer) {
      payload["draftCouponId"] = activityData._id;
    }
    if (activityData?._id) {
      payload["stayId"] = activityData._id;
    }
    cleanObject(payload);

    if (isEditActivity) {
      updateService(objectToFormData(payload), {
        onSuccess: async (res) => {
          successToast(res.result.message);
          handleImageClear();
          onSuccess?.();
        },
        onError: (err) => {
          errorToast(err?.response?.data?.result?.message);
        },
      });
    } else {
      addService(objectToFormData(payload), {
        onSuccess: async (res) => {
          successToast(res.result.message);
          await queryClient.refetchQueries({
            queryKey: ["my-stay-services"],
          });
          handleImageClear();
          const fileInput = document.getElementById(id || "services-img");
          if (fileInput) {
            (fileInput as HTMLInputElement).value = "";
          }
          clearValues();
          if (isDraftEditOffer || activityData?._id) onSuccess?.();
          else navigate(`/${params.hotelcode}/tv-app/my-stay/services`);
        },
        onError: (err) => {
          errorToast(err?.response?.data?.result?.message);
        },
      });
    }
  };

  return (
    <div>
      <div className="mt-1">
        {!isEditActivity && (
          <div className="flex gap-2 items-center">
            <Link
              to={`${process.env.PUBLIC_URL}/shmv/tv-app/my-stay/services`}
            >
              <LeftArrowSvg />
            </Link>
            <h3 className="text-[22px] font-normal text-[#1E2F65]">
              Services
            </h3>
          </div>
        )}
        <FormProvider {...methods}>
          {couponTypesDataLoading ||
          businessReasonDataLoading ||
          serviceCategoriesLoading ||
          costCenterLoading ||
          eligibilityCriteriaLoading ||
          updateLoading ||
          loading ? (
            <Spinner fullScreenOverlay className="z-[100]" />
          ) : null}
          <div className="grid grid-cols-1 relative mt-5">
            <TextInput
              name="name"
              rules={{
                required: "Service Name is required",
                maxLength: {
                  value: 30,
                  message: "Service Name cannot exceed 30 characters",
                },
              }}
              label="Service Name"
              placeholder="Zumba"
            />
          </div>
          <div className="grid grid-cols-1 relative mt-5">
            <TextArea
              name="description"
              rules={{
                required: "Service description is required",
                maxLength: {
                  value: 160,
                  message: "Service description cannot exceed 160 characters",
                },
              }}
              label="Service description"
              placeholder="Enjoy a $20 Food & Beverage credit at all outlets! Plus, get a free drink exclusively when using Shashi Cash.
                Don't miss out on this special offer!"
              className="w-full h-[75px] p-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 font-sans placeholder:text-[14px]"
            />
          </div>
          <div className="grid grid-cols-1 relative mt-5">
            <TextArea
              name="redemptionDescription"
              rules={{
                required: "Redemption description is required",
                maxLength: {
                  value: 60,
                  message: "Redemption description cannot exceed 60 characters",
                },
              }}
              label="Redemption description"
              placeholder="Must be charged to your room. Discount applied automatically at checkout."
              className="w-full h-[75px] p-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 font-sans placeholder:text-[14px]"
            />
          </div>
          <div className="mt-5">
            <label className="block text-sol text-sm text-[#666672]">
              Display On
            </label>
            <div className="flex items-center gap-3 mt-1">
              <Checkbox
                label="My Schedule"
                name="displayOn.MY_SCHEDULE"
                customClass="font-azo text-[16px] leading-[19.2px] tracking-[0] font-light"
                shape="rounded"
              />
              <Checkbox
                label="Recommendations"
                name="displayOn.RECOMMENDATIONS"
                customClass="font-azo text-[16px] leading-[19.2px] tracking-[0] font-light"
                shape="rounded"
              />
              <Checkbox
                label="Activity Calendar"
                name="displayOn.ACTIVITIES"
                customClass="font-azo text-[16px] leading-[19.2px] tracking-[0] font-light"
                shape="rounded"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 relative mt-5">
            <Select
              name="serviceCategory"
              rules={{ required: "Service category is required" }}
              label="Services Category"
              placeholder="Select service category"
              options={serviceCategoriesOptions}
            />
          </div>

          {eligibilityCriteriaFields.map((field, index) => {
            const selectedEligibility =
              eligibilityCriteriaData?.result?.data.find(
                (item) =>
                  item._id ===
                  eligibilityCriterias?.[index]?.eligibilityCriteria?.value
              );

            const valueTypeEligibility = DynamicMultiSelectKeys.includes(
              selectedEligibility?.key || ""
            )
              ? "DYNAMIC_MULTI_SELECT"
              : selectedEligibility?.valueType;

            const currentEligibilityData = eligibilityCriterias?.[index]?.data;
            const eligibilityCriteriaOptions =
              selectedEligibility?.options?.map(
                (option: { label: string; value: string }) => ({
                  label: option.label,
                  value: option.value,
                })
              ) || [];

            const selectedLabelsExcludingCurrent = eligibilityCriterias
              .filter((_, currentIndex) => currentIndex !== index)
              .map((item) => item.eligibilityCriteria?.label);

            return (
              <div key={field.id} className="grid grid-cols-3 gap-5 mt-5">
                <Select
                  label={`Eligibility Criteria ${index + 1}`}
                  name={`eligibilityCriterias.${index}.eligibilityCriteria`}
                  rules={{
                    required: "Eligibility Criteria is required",
                    validate: (selectedValue) =>
                      selectedValue?.value !== "" ||
                      "Eligibility Criteria is required",
                  }}
                  placeholder={`Select Eligibility Criteria ${index + 1}`}
                  options={
                    index === 1 &&
                    eligibilityCriterias.findIndex(
                      (item) => item.eligibilityCriteria?.label === RESERVATION_NO
                    ) !== -1
                      ? eligibilityOptions.filter(
                          (item) => item.label === MINIMUM_FOLIO_CHARGE
                        )
                      : eligibilityOptions.filter(
                          (item) =>
                            ![
                              ...(index === 0
                                ? []
                                : disableEligibilityFieldsAddOn),
                              ...selectedLabelsExcludingCurrent,
                            ].includes(item.label)
                        )
                  }
                  onChange={(selectedValue) => {
                    setValue(
                      `eligibilityCriterias.${index}.eligibilityCriteria`,
                      selectedValue
                    );
                    setValue(`eligibilityCriterias.${index}.data`, "");
                    setDynamicOptionsReset(true);
                  }}
                />
                <div className="col-start-2 -col-end-1 flex gap-4 [&>div]:flex-grow">
                  {valueTypeEligibility === "TEXT" && (
                    <TextInput
                      name={`eligibilityCriterias.${index}.data`}
                      value={
                        selectedEligibility?.key === "MINIMUM_FOLIO_CHARGE"
                          ? `$${
                              typeof currentEligibilityData === "string"
                                ? currentEligibilityData
                                : ""
                            }`
                          : String(
                              typeof currentEligibilityData === "string"
                                ? currentEligibilityData
                                : ""
                            )
                      }
                      onChange={(e) => {
                        const inputValue = e.target.value.replace(/\$/g, "");
                        setValue(`eligibilityCriterias.${index}.data`, inputValue);
                      }}
                      rules={{ required: "Value is required" }}
                      label="Select Values"
                      placeholder={`Enter ${selectedEligibility?.name || "value"}`}
                    />
                  )}

                  {valueTypeEligibility === "DYNAMIC_MULTI_SELECT" && (
                    <DynamicMultiSelect
                      name={`eligibilityCriterias.${index}.data`}
                      rules={{ required: "Value is required" }}
                      isMulti
                      label="Select Values"
                      placeholder={
                        DynamicMultiSelectKeys.includes(selectedEligibility?.key || "")
                          ? `Enter ${selectedEligibility?.name}`
                          : `Select ${selectedEligibility?.name}`
                      }
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      checkboxOptions={true}
                      wrapperComponentStyle="col-start-2 -col-end-1 min-w-[50%] w-fit"
                      dynamicOptionsReset={dynamicOptionsReset}
                      handleDynamicOptionsReset={handleDynamicOptionsReset}
                    />
                  )}

                  {valueTypeEligibility === "SINGLE_SELECT" && (
                    <Select
                      name={`eligibilityCriterias.${index}.data`}
                      rules={{ required: "Value is required" }}
                      label="Select Values"
                      placeholder={`Select ${selectedEligibility?.name}`}
                      options={eligibilityCriteriaOptions}
                    />
                  )}

                  {valueTypeEligibility === "MULTI_SELECT" && (
                    <Select
                      name={`eligibilityCriterias.${index}.data`}
                      rules={{ required: "At least one value must be selected" }}
                      isMulti
                      label="Select Values"
                      placeholder={`Select ${selectedEligibility?.name}`}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      checkboxOptions={true}
                      wrapperComponentStyle="col-start-2 -col-end-1 min-w-[50%] w-fit"
                      options={eligibilityCriteriaOptions}
                    />
                  )}

                  {index === 0 ? (
                    !!valueTypeEligibility &&
                    (!disableEligibilityFieldsAddOn.includes(
                      selectedEligibility?.name || ""
                    ) ||
                      (disableEligibilityFieldsAddOn.includes(
                        selectedEligibility?.name || ""
                      ) &&
                        eligibilityCriterias.length !== 2)) ? (
                      <AddEligibilitySvg
                        className="mt-6 cursor-pointer"
                        onClick={eligibilityCriteriaAppendField}
                      />
                    ) : null
                  ) : (
                    <RemoveEligibilitySvg
                      className="mt-6 cursor-pointer"
                      onClick={() => eligibilityCriteriaRemoveField(index)}
                    />
                  )}
                </div>
              </div>
            );
          })}

          <div className="grid grid-cols-3 gap-3 relative mt-5">
            <Select
              name="type"
              rules={{ required: "Service Type is required" }}
              label="Service Type"
              placeholder="Select Service Type"
              options={offerTypeOptions}
              value={type}
              onChange={(data) => {
                setValue("type", data);
                setValue("value", null);
                setValue("noOfGuest", null);
              }}
            />
            <TextInput
              name="value"
              label="Enter Value"
              placeholder="$50"
              value={value !== undefined && value !== null ? `$${value}` : ""}
              onChange={(e) => {
                const inputValue = e.target.value.replace(/[^0-9.]/g, "");
                setValue("value", inputValue ? +inputValue : null);
                setValue("noOfGuest", null);
              }}
            />

            {type?.label === "Group" && (
              <TextInput
                name="noOfGuest"
                rules={{ required: "No Of Guest is required" }}
                label="Enter No Of Guest"
                placeholder="Enter No Of Guest"
                value={noOfGuest ?? ""}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  setValue("noOfGuest", +inputValue);
                }}
              />
            )}

            <Select
              name="businessReason"
              rules={{ required: "Value is required" }}
              label="Business Reason"
              placeholder="Business Reason"
              options={businessReasonOptions}
            />
          </div>
          <h3 className="text-[22px] font-normal text-[#1E2F65] mt-5">
            Offers Management
          </h3>
          <div className="grid grid-cols-3 gap-3 relative mt-5">
            <Select
              name="redemptionLocation"
              rules={{ required: "Value is required" }}
              label="Redemption Location"
              placeholder="Select activity"
              options={redemptionLocationOptions}
            />
          </div>

          <div className="grid grid-cols-3 gap-3 relative mt-5">
            <DatePicker
              name="startDate"
              rules={{ required: "Start Date is required" }}
              type="date"
              label="Start Date"
              placeholder="03: 00 PM"
              min={new Date().toLocaleString("sv").split(" ")[0]}
            />
            <DatePicker
              name="endDate"
              rules={{ required: "End Date is required" }}
              type="date"
              label="End Date"
              placeholder="06: 00 PM"
              min={startDate}
            />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <TimeInput
              name="startTime"
              rules={{ required: "Start Time is required" }}
              type="time"
              label="Service Start Time"
              placeholder="03: 00 PM"
            />

            <TimeInput
              name="endTime"
              rules={{
                required: "End Time is required",
                validate: validateEndTime(startDate, endDate, startTime),
              }}
              type="time"
              label="Service End Time"
              placeholder="06: 00 PM"
            />
          </div>
          <div className="grid grid-cols-6 mt-6.5 gap-5">
            <div className="col-start-1 -col-end-3 flex gap-4 [&>div]:flex-grow">
              <Select
                name="days"
                isMulti
                label="Days"
                placeholder="Select Days"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                checkboxOptions={true}
                options={WEEK_DAYS}
                value={days.sort((a, b) => a.id - b.id)}
                useFieldOnChange={false}
                onChange={(data) => {
                  if (
                    data.find(
                      (ele: any) => ele.id == 0 && ele.value == "Everyday"
                    )
                  ) {
                    setIsEveryDaySelected(true);
                    setValue("days", WEEK_DAYS);
                  } else if (
                    isEverydaySelected &&
                    !data.find(
                      (ele: any) => ele.id == 0 && ele.value == "Everyday"
                    )
                  ) {
                    setValue("days", []);
                    setIsEveryDaySelected(false);
                  } else {
                    setValue("days", [...data]);
                  }
                }}
              />
            </div>
          </div>
          <div className="mt-7">
            <div className="grid grid-cols-3 gap-3 mt-5">
              <FileInput
                id="services-img"
                name="image"
                rules={{ required: "Image is required" }}
                label="Offer Image"
                type="file"
                accept="image/*"
                className="hidden w-[240px] text-[10px] leading-[18px] text-[#666672] border border-[#D9D9D9] rounded cursor-pointer focus:outline-none file:bg-[#F41095] file:box-border file:p-2 file:text-[10px] file:leading-[18px] file:outline-none file:border-none file:border-r file:border-[#D9D9D9] file:text-white"
                onChange={handleFileChange}
                onClear={handleImageClear}
                selectedFileName={selectedFileName}
                previousImage={activityData.image ? (image as string) : ""}
              />
            </div>

            {image ? (
              <div className="relative my-4">
                <img
                  src={
                    typeof image === "string"
                      ? image
                      : URL.createObjectURL(image)
                  }
                  alt="Cropped"
                  height={"auto"}
                  width={"229px"}
                  className="mx-auto"
                />
              </div>
            ) : activityData.image && image ? (
              <img
                src={image}
                alt="Cropped"
                height={"auto"}
                width={"229px"}
                className="mx-auto"
              />
            ) : null}

            {imageFile && (
              <ImageCropModal
                isOpen={isCropModalOpen}
                image={imageFile}
                onClose={() => setIsCropModalOpen(false)}
                onCropComplete={handleCropComplete}
              />
            )}
          </div>

          <div className="flex items-center justify-end gap-4 mt-10">
            {!isEditActivity && (
              <Button
                className="w-[130px]"
                onClick={saveAsDraft}
                loading={addDraftServiceLoading || updateDraftServiceLoading}
              >
                {isDraftEditOffer ? "Update Draft" : "Save as Draft"}
              </Button>
            )}
            <DiscardSaveButtons
              onDiscard={() => {
                setValue("image", null);
                const fileInput = document.getElementById(
                  id || "services-img"
                );
                if (fileInput) {
                  (fileInput as HTMLInputElement).value = "";
                }
                if (isEditActivity) {
                  setValue("image", null);
                }
                handleImageClear();
                setIsCropModalOpen(false);
                clearValues();
              }}
              onSave={handleSubmit(onSubmit)}
              loading={loading}
              saveBtnOptions={{
                name: isEditActivity ? "Update" : "Save",
                className: "w-32",
              }}
            />
          </div>
        </FormProvider>
      </div>
    </div>
  );
};

export default CreateService;
